import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { CountryFlag } from '../CountryFlag';
import { TeamShirt } from '../TeamShirt';
import { Typography } from '../Typography';

export type TeamOverviewFormResult = 'win' | 'lose' | 'draw';

export interface TeamOverviewFormItem {
  result: TeamOverviewFormResult;
  isLatest?: boolean;
}

export interface TeamOverviewTeamInfo {
  name: string;
  country: string;
  form: TeamOverviewFormItem[];
}

export interface TeamOverviewComparisonRow {
  label: string;
  leftPlayerName: string;
  leftPlayerCountry: string;
  leftValue: string;
  rightValue: string;
  rightPlayerName: string;
  rightPlayerCountry: string;
}

export interface TeamOverviewProps {
  title?: string;
  leftTeam: TeamOverviewTeamInfo;
  rightTeam: TeamOverviewTeamInfo;
  rows: TeamOverviewComparisonRow[];
  className?: string;
  /** When set, the card renders only the visible side's data. */
  hideSide?: 'left' | 'right';
}

const formTagStyleMap: Record<
  TeamOverviewFormResult,
  {
    label: string;
    chipClassName: string;
    markerClassName: string;
  }
> = {
  win: {
    label: 'W',
    chipClassName: 'bg-[#8ce28b] text-[#222526]',
    markerClassName: 'bg-[#8ce28b]',
  },
  lose: {
    label: 'L',
    chipClassName: 'bg-[#db2b31] text-white',
    markerClassName: 'bg-[#db2b31]',
  },
  draw: {
    label: 'D',
    chipClassName: 'bg-[#bebfbf] text-[#757777]',
    markerClassName: 'bg-[#bebfbf]',
  },
};

interface TeamOverviewFormTagProps {
  item: TeamOverviewFormItem;
}

const TeamOverviewFormTag: FC<TeamOverviewFormTagProps> = ({ item }) => {
  const tagStyle = formTagStyleMap[item.result];

  return (
    <div className="flex w-[18px] flex-col items-start gap-[3px]">
      <div
        className={twMerge(
          'flex h-[18px] w-[18px] items-center justify-center rounded-[--corner-xs]',
          tagStyle.chipClassName
        )}
      >
        <Typography as="span" variant="button010" className="!text-[10px] !leading-[10px]">
          {tagStyle.label}
        </Typography>
      </div>
      {item.isLatest ? (
        <div className={twMerge('h-[2px] w-full rounded-full', tagStyle.markerClassName)} />
      ) : null}
    </div>
  );
};

const TeamHeaderBlock: FC<{ team: TeamOverviewTeamInfo; side: 'left' | 'right' }> = ({
  team,
  side,
}) => (
  <div
    className={twMerge(
      'flex min-w-0 items-center gap-[--padding-xs]',
      side === 'right' && 'flex-row-reverse'
    )}
  >
    <div className="size-[20px] shrink-0 overflow-hidden rounded-full">
      <CountryFlag className="h-full w-full object-cover" country={team.country} />
    </div>
    <Typography variant="subheadline020" className="truncate text-[--color-neutral-900]">
      {team.name}
    </Typography>
  </div>
);

const ComparisonRow: FC<{ row: TeamOverviewComparisonRow }> = ({ row }) => (
  <div className="grid grid-cols-[1fr_40px_40px_1fr] items-center gap-[--padding-xs] px-[--padding-sm] py-[--padding-sm]">
    <div className="flex min-w-0 items-center gap-[--padding-xs]">
      <TeamShirt
        team={row.leftPlayerCountry}
        assetScale="1x"
        className="h-[32px] w-[32px] shrink-0"
      />
      <Typography variant="subheadline020" className="truncate text-[--color-neutral-900]">
        {row.leftPlayerName}
      </Typography>
    </div>

    <Typography
      variant="subheadline020"
      className="text-center whitespace-nowrap text-[--color-neutral-900]"
    >
      {row.leftValue}
    </Typography>

    <Typography
      variant="subheadline020"
      className="text-center whitespace-nowrap text-[--color-neutral-900]"
    >
      {row.rightValue}
    </Typography>

    <div className="flex min-w-0 items-center justify-end gap-[--padding-xs]">
      <Typography variant="subheadline020" className="truncate text-[--color-neutral-900]">
        {row.rightPlayerName}
      </Typography>
      <TeamShirt
        team={row.rightPlayerCountry}
        assetScale="1x"
        className="h-[32px] w-[32px] shrink-0"
      />
    </div>
  </div>
);

const SingleTeamRow: FC<{ row: TeamOverviewComparisonRow; side: 'left' | 'right' }> = ({
  row,
  side,
}) => {
  if (side === 'left') {
    return (
      <div className="flex items-center justify-between px-[--padding-sm] py-[--padding-sm]">
        <div className="flex min-w-0 items-center gap-[--padding-xs]">
          <TeamShirt
            team={row.leftPlayerCountry}
            assetScale="1x"
            className="h-[32px] w-[32px] shrink-0"
          />
          <Typography variant="subheadline020" className="truncate text-[--color-neutral-900]">
            {row.leftPlayerName}
          </Typography>
        </div>
        <Typography
          variant="subheadline020"
          className="text-center whitespace-nowrap text-[--color-neutral-900]"
        >
          {row.leftValue}
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-[--padding-sm] py-[--padding-sm]">
      <Typography
        variant="subheadline020"
        className="text-center whitespace-nowrap text-[--color-neutral-900]"
      >
        {row.rightValue}
      </Typography>
      <div className="flex min-w-0 items-center justify-end gap-[--padding-xs]">
        <Typography variant="subheadline020" className="truncate text-[--color-neutral-900]">
          {row.rightPlayerName}
        </Typography>
        <TeamShirt
          team={row.rightPlayerCountry}
          assetScale="1x"
          className="h-[32px] w-[32px] shrink-0"
        />
      </div>
    </div>
  );
};

export const TeamOverview: FC<TeamOverviewProps> = ({
  title = 'TEAM OVERVIEW',
  leftTeam,
  rightTeam,
  rows,
  className,
  hideSide,
}) => {
  const isSingleTeam = !!hideSide;
  const activeSide = hideSide === 'left' ? 'right' : 'left';

  return (
    <div
      className={twMerge(
        'w-full rounded-[--corner-md] bg-white p-[--padding-md] shadow-[0px_2px_8px_0px_rgba(34,37,38,0.08)]',
        className
      )}
    >
      <div className="flex flex-col gap-[--padding-md]">
        <Typography
          variant="label050"
          className="!font-[800] text-[--color-neutral-900] text-[20px]"
        >
          {title}
        </Typography>

        <div className="rounded-[--corner-sm] bg-[--page-header-bg-grey]">
          <div className="flex items-center justify-between px-[--padding-sm] py-[--padding-sm]">
            <TeamHeaderBlock team={leftTeam} side="left" />
            <TeamHeaderBlock team={rightTeam} side="right" />
          </div>

          <div className="border-t border-[--playerfilters-border] px-[--padding-sm] py-[--padding-sm]">
            {isSingleTeam ? (
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-[--padding-md]">
                <div
                  className={twMerge(
                    'flex items-center gap-[--padding-2xs]',
                    activeSide === 'left' ? 'justify-start' : 'justify-end'
                  )}
                >
                  {activeSide === 'left' &&
                    leftTeam.form.map((item, index) => (
                      <TeamOverviewFormTag key={`${item.result}-${index}`} item={item} />
                    ))}
                </div>
                <Typography
                  variant="paragraph000"
                  className="text-center text-[--color-neutral-900]"
                >
                  Form
                </Typography>
                <div
                  className={twMerge(
                    'flex items-center gap-[--padding-2xs]',
                    activeSide === 'right' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {activeSide === 'right' &&
                    rightTeam.form.map((item, index) => (
                      <TeamOverviewFormTag key={`${item.result}-${index}`} item={item} />
                    ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-[--padding-md]">
                <div className="flex items-center gap-[--padding-2xs]">
                  {leftTeam.form.map((item, index) => (
                    <TeamOverviewFormTag key={`${item.result}-${index}`} item={item} />
                  ))}
                </div>
                <Typography
                  variant="paragraph000"
                  className="text-center text-[--color-neutral-900]"
                >
                  Form
                </Typography>
                <div className="flex items-center justify-end gap-[--padding-2xs]">
                  {rightTeam.form.map((item, index) => (
                    <TeamOverviewFormTag key={`${item.result}-${index}`} item={item} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-[--corner-sm]">
          {rows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className={twMerge(
                'border-t border-[--playerfilters-border] bg-white',
                index === 0 && 'border-t-0'
              )}
            >
              <div className="px-[--padding-sm] py-[--padding-xs]">
                <Typography
                  variant="paragraph000"
                  className="text-center text-[--color-neutral-900]"
                >
                  {row.label}
                </Typography>
              </div>
              {isSingleTeam ? (
                <SingleTeamRow row={row} side={activeSide} />
              ) : (
                <ComparisonRow row={row} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
